import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { deductCallCreditAction } from '../../../../../shared/actions/creditAction';
import {
  formatCreditsValue,
  LOW_BALANCE_WARNING_MINUTES,
  getCallRequestPricingDetails,
} from '../utils/callFormatUtils';
import { CREDITS_PER_USD, normalizeCallKind } from '../constant';
import { formatAmountWithTwoDecimalPlaces } from '../../../../../shared/utils/function';
import { showError } from '../../../../../shared/utils/swalHandler';

const MIN_TIP_CREDITS = 8.5;
const MAX_TIP_CREDITS = 200;
const DEFAULT_TIP_CREDITS = 8.5;
const PRESET_CREDIT_AMOUNTS = [20, 50, 100];

const clampCredits = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return MIN_TIP_CREDITS;
  return Math.min(MAX_TIP_CREDITS, Math.max(MIN_TIP_CREDITS, n));
};

const TipCreditModal = ({
  isOpen = false,
  onClose,
  creatorInfo,
  creatorId,
  callMediaType = 'video',
  onTipSuccess,
  openTipModalRef,
  onOpenCredits,
}) => {
  const dispatch = useDispatch();
  const walletCredits = useSelector((state) => Number(state.credit?.walletBalance?.credits) || 0);
  const [tipCredits, setTipCredits] = useState(DEFAULT_TIP_CREDITS);
  const [submitting, setSubmitting] = useState(false);
  const latestTipRef = useRef(tipCredits);
  const normalizedCallKind = normalizeCallKind(callMediaType);

  const callPricing = useMemo(
    () => getCallRequestPricingDetails(creatorInfo, normalizedCallKind),
    [creatorInfo, normalizedCallKind]
  );
  const requiredCallReserveCredits = useMemo(() => {
    if (!callPricing?.ok) return null;
    if (callPricing.pricingModel === 'flat_rate') {
      const blockCredits = callPricing.flatRateCredits ?? callPricing.sessionCostCredits;
      return Number.isFinite(blockCredits) && blockCredits > 0 ? blockCredits : null;
    }
    const perMinuteCredits = Number(callPricing.ratePerMinuteCredits);
    if (!Number.isFinite(perMinuteCredits) || perMinuteCredits <= 0) return null;
    return perMinuteCredits * LOW_BALANCE_WARNING_MINUTES;
  }, [callPricing]);

  useEffect(() => {
    if (!isOpen) return;
    const initial = clampCredits(DEFAULT_TIP_CREDITS);
    setTipCredits(initial);
    latestTipRef.current = initial;
  }, [isOpen]);

  const tipUsd = useMemo(() => Number(tipCredits) / CREDITS_PER_USD, [tipCredits]);
  const creatorHandle = useMemo(() => creatorInfo?.username || 'creator', [creatorInfo]);

  const handleCreditsChange = useCallback((rawValue) => {
    const next = clampCredits(rawValue);
    latestTipRef.current = next;
    setTipCredits(next);
  }, []);

  const sendTip = useCallback(async () => {
    const selectedCredits = clampCredits(latestTipRef.current);
    if (selectedCredits <= 0) return;
    if (walletCredits < selectedCredits) {
      await showError('Insufficient Credits', 'Please add more credits to continue.');
      return;
    }
    if (Number.isFinite(requiredCallReserveCredits)) {
      const balanceAfterTip = walletCredits - selectedCredits;
      if (balanceAfterTip < requiredCallReserveCredits) {
        const reserveLabel =
          callPricing.pricingModel === 'flat_rate'
            ? `${formatCreditsValue(requiredCallReserveCredits)} credits for your next session block`
            : `at least ${LOW_BALANCE_WARNING_MINUTES} minutes of call credits`;
        await showError(
          'Top Up Required',
          `Please top up first to keep ${reserveLabel} before sending a tip.`
        );
        onOpenCredits?.();
        return;
      }
    }
    if (!creatorId) {
      await showError('Error', 'Creator details are missing. Please try again.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        creatorId: String(creatorId),
        // Keep API tip credit exactly aligned with what user sees in UI.
        credit: String(formatAmountWithTwoDecimalPlaces(selectedCredits)),
        callInMinutes: 0,
        callInSec: 0,
        callMediaType: normalizedCallKind === 'voice' ? 'audio' : 'video',
        type: 'tip_call',
      };
      const response = await dispatch(deductCallCreditAction(payload));
      if (!response?.success) {
        await showError('Tip Failed', response?.error || 'Unable to send tip right now.');
        return;
      }

      const tipMessage = `Sent a tip of ${selectedCredits} credits`;
      const tipMeta = {
        amount: Number(formatAmountWithTwoDecimalPlaces(tipUsd)),
        baseAmount: Number(formatAmountWithTwoDecimalPlaces(tipUsd)),
        realPrice: Number(formatAmountWithTwoDecimalPlaces(tipUsd)),
        tip_type: 'tip_call',
        creditAmount: Number(selectedCredits),
        creatorId: String(creatorId),
      };

      onTipSuccess?.(tipMessage, tipMeta);
      onClose?.();
    } finally {
      setSubmitting(false);
    }
  }, [
    walletCredits,
    requiredCallReserveCredits,
    onOpenCredits,
    creatorId,
    normalizedCallKind,
    dispatch,
    tipUsd,
    onTipSuccess,
    onClose,
  ]);

  useEffect(() => {
    const api = openTipModalRef?.current;
    if (!api || !creatorId) return undefined;
    api.sendTipWithAmount = async (amount) => {
      handleCreditsChange(amount);
      latestTipRef.current = clampCredits(amount);
      await sendTip();
    };
    return () => {
      api.sendTipWithAmount = null;
    };
  }, [openTipModalRef, creatorId, handleCreditsChange, sendTip]);

  if (!isOpen) return null;
  const modalRoot = (typeof document !== 'undefined' && document.body) || null;
  if (!modalRoot) return null;

  return createPortal(
    <div className="cm_call_tip_credit_overlay cm_bg_blur">
      <div className="cm_call_tip_credit_popup">
        <div className="cm_call_tip_credit_header">
          <div className="cm_call_tip_credit_profile">
            <img src={creatorInfo?.profile_picture} alt="Creator Avatar" className="cm_call_tip_credit_avatar" />
            <span className="cm_call_tip_credit_username">@{creatorHandle}</span>
          </div>
          <div
            className="cm_call_tip_credit_balance"
            role="button"
            tabIndex={0}
            onClick={() => {
              if (!submitting) onOpenCredits?.();
            }}
            onKeyDown={(e) => {
              if (submitting) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpenCredits?.();
              }
            }}
          >
            + Credits: {formatAmountWithTwoDecimalPlaces(walletCredits)}
          </div>
        </div>

        <div className="cm_call_tip_credit_amount_row">
          <span>Tip Amount</span>
          <strong>{formatAmountWithTwoDecimalPlaces(tipCredits)} Credits</strong>
        </div>




        <div className="cm_call_tip_credit_slider_wrap">
          <input
            type="range"
            min={MIN_TIP_CREDITS}
            max={MAX_TIP_CREDITS}
            step="0.01"
            value={tipCredits}
            className="cm_call_tip_credit_slider"
            style={{
              '--slider-fill': `${((tipCredits - MIN_TIP_CREDITS) / (MAX_TIP_CREDITS - MIN_TIP_CREDITS)) * 100}%`,
            }}
            onChange={(e) => handleCreditsChange(e.target.value)}
          />
          <div className="cm_call_tip_credit_labels">
            <span>{formatAmountWithTwoDecimalPlaces(MIN_TIP_CREDITS)}</span>
            <span>{Math.floor(MAX_TIP_CREDITS / 2)}</span>
            <span>{MAX_TIP_CREDITS}</span>
          </div>
        </div>

        <div className="cm_call_tip_credit_presets">
          {PRESET_CREDIT_AMOUNTS.map((amount) => (
            <button
              key={amount}
              type="button"
              className={`cm_call_tip_credit_preset_btn ${Number(tipCredits) === amount ? 'active' : ''}`}
              onClick={() => handleCreditsChange(amount)}
            >
              {amount}
            </button>
          ))}
        </div>

        <button type="button" className="cm_call_tip_credit_send_btn" onClick={sendTip} disabled={submitting}>
          <i className="fa-solid fa-heart" /> {submitting ? 'Sending...' : 'Send Tip'}
        </button>
        <button type="button" className="cm_call_tip_credit_cancel_btn" onClick={onClose} disabled={submitting}>
          Cancel
        </button>
        <div className="cm_call_tip_credit_footer">
          <i className="fa-solid fa-shield-halved" /> Secure payment powered by CCBill
        </div>
      </div>
    </div>,
    modalRoot
  );
};

export default TipCreditModal;
